<template lang="html">
  <NuxtLink
    :to="link"
    class="group category-card flex h-full flex-col overflow-hidden border border-default bg-default transition-[border-color,box-shadow,transform] duration-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none"
    :class="cardClass"
  >
    <div
      class="relative w-full flex-none overflow-hidden bg-elevated"
      :class="mediaClass"
    >
      <UiPicture
        v-if="coverId"
        class="size-full transition-transform duration-200 group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
        :class="imageClass"
        :src="coverId"
        variant="imageFull"
        :alt="category.name"
        :sizes="imageSizes"
        :loading="eager ? undefined : 'lazy'"
      />
      <div
        v-else
        class="flex size-full items-center justify-center"
        aria-hidden="true"
      >
        <UIcon
          name="i-lucide-image"
          class="text-muted opacity-40"
          :class="placeholderIconClass"
        />
      </div>
    </div>
    <div
      class="flex flex-1 flex-col border-t border-default"
      :class="titleBarClass"
    >
      <span
        class="category-card__title max-h-[2lh] overflow-hidden text-ellipsis text-highlighted transition-colors group-hover:text-primary"
        :class="titleClass"
      >
        {{ category.name }}
      </span>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
/**
 * Shared category card atom (Phase 2 root / Phase 6 hub / Phase 8 home).
 * Image + name; destination via shared resolver. `size` switches density:
 * root = compact catalog/home tiles; hub = denser retail routing tiles.
 * Media is flex-none; title bar flex-1 so stretched grid rows don’t leave a
 * mid-card gap. Title clamps to 2 lines (2lh).
 */
import type { CategoryDto } from '#Products/models/Pim/CategoryDto'
import UiPicture from '~/app/components/Image/UiPicture.vue'

const props = withDefaults(
  defineProps<{
    category: CategoryDto
    /** Density: root/home compact vs hub retail tiles */
    size?: 'root' | 'hub'
    /** First-row / LCP candidates load eagerly */
    eager?: boolean
  }>(),
  {
    size: 'root',
    eager: false,
  },
)

const { resolve } = useCategoryDestination()

const link = computed(() => resolve(props.category).route)
const coverId = computed(() => props.category.images?.[0])
const isHub = computed(() => props.size === 'hub')

const cardClass = computed(() =>
  isHub.value
    ? 'rounded-md shadow-xs hover:border-primary hover:shadow-sm'
    : 'rounded-md hover:border-primary hover:shadow-xs',
)

const mediaClass = computed(() =>
  isHub.value
    ? 'aspect-square p-2 sm:p-3'
    : 'aspect-square',
)

const imageClass = computed(() =>
  isHub.value
    ? 'object-contain'
    : 'object-cover',
)

const imageSizes = computed(() =>
  isHub.value
    ? '50vw sm:33vw lg:33vw xl:25vw'
    : '50vw md:33vw lg:25vw',
)

const placeholderIconClass = computed(() =>
  isHub.value
    ? 'size-10 sm:size-12'
    : 'size-10',
)

const titleBarClass = computed(() =>
  isHub.value
    ? 'px-2.5 py-2 text-center sm:px-3 sm:py-2.5'
    : 'px-2 py-2 sm:px-2.5 sm:py-2.5',
)

const titleClass = computed(() =>
  isHub.value
    ? 'text-sm font-semibold'
    : 'text-sm font-medium group-hover:underline',
)
</script>

<style scoped>
.category-card__title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}
</style>
